"use client";

import { Layout } from "antd";
import { useState, useMemo, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import SideBar from "../SideBar/SideBar";
import Navbar from "../Navbar/Navbarpatient";

import MedicationToastBridge from "../../notifications/components/MedicationToastBridge";
import RendezvousToastBridge from "../../notifications/components/RendezvousToastBridge";

import useLoadFollows from "../../ProfileDoctor/hooks/useLoadFollows";

import "./Layout.css";

const { Sider, Content } = Layout;
const SIDER_W = 240;
const SIDER_W_COLLAPSED = 80;
const MOBILE_BREAKPOINT = 768;
const SIDEBAR_COLLAPSED_KEY = "sb_collapsed";

/* ─── Pages qui doivent bypasser le layout sur mobile ─── */
const FULLSCREEN_MOBILE_PATHS = ["/messaging", "/messages"];
function SiderToggleBtn({ collapsed, onClick, className = "" }) {
  const { t } = useTranslation();
  return (
    <button
      className={`sider-toggle ${collapsed ? "is-collapsed" : ""} ${className}`}
      type="button"
      aria-label={collapsed ? t("layout.sidebar.open") : t("layout.sidebar.close")}
      aria-expanded={!collapsed}
      onClick={onClick}
    >
      <svg
        viewBox="0 0 24 24"
        className={`sider-toggle__ico ${collapsed ? "to-right" : "to-left"}`}
        aria-hidden="true"
      >
        <path d="M9 6l6 6-6 6" />
      </svg>
    </button>
  );
}

export default function AppLayout({ children }) {
  const { i18n } = useTranslation();
  const location = useLocation();

  useLoadFollows();

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "true";
    } catch {
      return false;
    }
  });

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isRTL, setIsRTL] = useState(i18n.language === "ar");

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      } catch {}
      return next;
    });
  }, []);

  useEffect(() => {
    const handleLanguageChanged = () => {
      const rtl = i18n.language === "ar";
      setIsRTL(rtl);
      document.documentElement.dir = rtl ? "rtl" : "ltr";
      document.documentElement.lang = i18n.language;
    };
    handleLanguageChanged();
    i18n.on("languageChanged", handleLanguageChanged);
    return () => { i18n.off("languageChanged", handleLanguageChanged); };
  }, [i18n]);

  const isCyclePage = useMemo(() => {
    const path = (location?.pathname || "").toLowerCase();
    const CYCLE_PATHS = ["/cycle", "/patient/cycle", "/calendar/cycle"];
    return CYCLE_PATHS.some((p) => path === p || path.startsWith(p + "/"));
  }, [location?.pathname]);

  const customNavbarStyle = useMemo(() => {
    if (!isCyclePage) return null;
    return {
      className: "app-header--cycle",
      style: {
        background: "linear-gradient(91.63deg, rgba(255, 182, 193, 0.5) -4.01%, #eec1c8 90.53%)",
      },
    };
  }, [isCyclePage]);

  useEffect(() => {
    const checkMobile = () => {
      const mobile = window.innerWidth <= MOBILE_BREAKPOINT;
      setIsMobile(mobile);
      if (!mobile && mobileMenuOpen) setMobileMenuOpen(false);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, [mobileMenuOpen]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "auto";
    return () => { document.body.style.overflow = "auto"; };
  }, [mobileMenuOpen]);

  const dynamicVars = useMemo(
    () => ({
      "--page-max": collapsed ? "1360px" : "1224px",
      "--tabs-side-pad": collapsed ? "28px" : "34px",
      "--tab-gap": collapsed ? "160px" : "200px",
    }),
    [collapsed]
  );

  const handleMobileMenuToggle = () => setMobileMenuOpen((prev) => !prev);
  const closeMobileMenu = () => setMobileMenuOpen(false);

  /* ─────────────────────────────────────────────────────────
     BYPASS LAYOUT : sur mobile + page messaging
     → on rend uniquement les children, plein écran
     → pas de sidebar, pas de navbar, pas de footer
  ───────────────────────────────────────────────────────── */
  const isFullscreenMobilePage = useMemo(() => {
    if (!isMobile) return false;
    const path = (location?.pathname || "").toLowerCase();
    return FULLSCREEN_MOBILE_PATHS.some(
      (p) => path === p || path.startsWith(p + "/")
    );
  }, [isMobile, location?.pathname]);

  if (isFullscreenMobilePage) {
    /* Rendu minimaliste : juste les children, le composant
       MessagingPatient/MessagingDoctor gère son propre plein écran */
    return <>{children}</>;
  }

  /* ─── Rendu normal (desktop ou mobile hors messaging) ─── */
  return (
    <Layout className={`app-layout ${isRTL ? "rtl" : "ltr"}`}>
      <Layout className="app-body">
        {isMobile && mobileMenuOpen && (
          <div
            className="mobile-sidebar-backdrop"
            onClick={closeMobileMenu}
            aria-hidden="true"
          />
        )}

        <Sider
          className={`app-sider ${collapsed ? "ant-layout-sider-collapsed" : ""} ${
            isMobile ? (mobileMenuOpen ? "mobile-open" : "mobile-closed") : ""
          }`}
          width={SIDER_W}
          collapsedWidth={SIDER_W_COLLAPSED}
          collapsed={isMobile ? false : collapsed}
          trigger={null}
        >
          <SideBar
            collapsed={isMobile ? false : collapsed}
            onMenuClick={isMobile ? closeMobileMenu : undefined}
          />
          {!isMobile && (
            <SiderToggleBtn
              collapsed={collapsed}
              onClick={toggleCollapsed}
            />
          )}
        </Sider>

        <Layout
          className={`app-main-content ${collapsed ? "is-collapsed" : ""} ${
            isMobile ? "is-mobile" : ""
          }`}
          style={dynamicVars}
        >
          <div
            className={`navbar-container ${isCyclePage ? "navbar-container--cycle" : ""}`}
          >
            <Navbar
              mode="patient"
              collapsed={collapsed}
              toggleCollapsed={toggleCollapsed}
              isMobile={isMobile}
              onMobileMenuToggle={handleMobileMenuToggle}
              customStyle={customNavbarStyle}
              isCyclePage={isCyclePage}
            />
          </div>
          <Content className="app-layout__content" style={{ paddingBottom: 56 }}>
            {children}
          </Content>
        </Layout>
      </Layout>

      <MedicationToastBridge />
      <RendezvousToastBridge />
    </Layout>
  );
}